import { Box } from '@customink/pigment-react';
import { Dialog } from '@customink/pigment-react/lib/components/Dialog';
import {
  type ProductIds,
  type ProjectItem,
  useConfigContext,
  useProject,
} from '@customink/project-service-react-components';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PropagateLoader } from 'react-spinners';

import { catalogIframeAtom } from './util';

import { rollbar } from '@/config/rollbar.config';
import { useCreateItem } from '@/hooks/ProjectItem';
import { themeColor } from '@/utils/designTokens';

export default function CatalogIframe() {
  const [{ filters, designCid, url, show: showIframe }, setCatalogState] =
    useAtom(catalogIframeAtom);

  const { catalogUrl } = useConfigContext();
  const { data: project } = useProject();
  const [createProjectItem, { isError, isLoading, isSuccess }] =
    useCreateItem();
  const [selectedProduct, setSelectedProduct] = useState<ProductIds>();

  const onClose = useCallback(
    () => setCatalogState({ show: false }),
    [setCatalogState],
  );

  const catalogParams = new URLSearchParams({ iframer: 'true' });
  const filtersMap = new Map(Object.entries(filters || {}));

  // set a param for each filter
  filtersMap.forEach((value, key) => {
    catalogParams.set(key, String(value));
  });

  const iframeSrc = `${url || catalogUrl}/?${catalogParams.toString()}`;

  const priority = project?.items?.find(
    (item) => item?.design?.cid === designCid,
  )?.fulfillment?.priority;

  useEffect(() => {
    if (!project?.id || !selectedProduct) return;

    const draftItem: Partial<ProjectItem> = {
      product: selectedProduct,
      fulfillment: {
        priority,
      },
    };

    if (designCid) {
      draftItem.design = { cid: designCid };
    }

    createProjectItem(draftItem);
  }, [designCid, project?.id, selectedProduct, priority, createProjectItem]);

  const handleMessage = useCallback((event: MessageEvent) => {
    if (event.data.event !== 'catalog:product-selected') return;
    // FIXME: for security reasons, verify event.origin and event.source

    const colorId = event.data.colorId ? Number(event.data.colorId) : undefined;
    const styleId = event.data.styleId;

    if (!colorId || !styleId) {
      rollbar.error(
        'CatalogIframe: product-selected event missing colorId or styleId',
      );
      return;
    }

    setSelectedProduct({
      colorId,
      styleId,
    });
  }, []);

  useEffect(() => {
    // wait to close until request finishes
    if (isSuccess) {
      // also close "Add Design to More Styles" accordion?
      onClose();
      setSelectedProduct(undefined);
    } else if (isError) {
      // if request fails, clear selected product but do not close modal
      // TODO: is this right behavior for this error case?
      setSelectedProduct(undefined);
    }
  }, [isError, isSuccess, onClose]);

  useEffect(() => {
    const eventType = 'message';

    if (showIframe) {
      window.addEventListener(eventType, handleMessage);
    } else if (showIframe === false) {
      window.removeEventListener(eventType, handleMessage);
    }

    return () => {
      window.removeEventListener(eventType, handleMessage);
    };
  }, [showIframe, handleMessage]);

  const modalContents = useMemo(
    () =>
      isLoading ? (
        <Box
          alignItems="center"
          display="flex"
          height="100%"
          justifyContent="center"
        >
          <PropagateLoader color={themeColor} loading={isLoading} />
        </Box>
      ) : (
        <iframe
          allowFullScreen
          seamless
          src={iframeSrc}
          style={{
            display: 'flex',
            overflowX: 'hidden',
            overflowY: 'auto',
            width: '100%',
            height: '100%',
            border: '0',
            borderTop: '1px solid #E3E3E3',
          }}
          tabIndex={0}
        ></iframe>
      ),
    [iframeSrc, isLoading],
  );

  const renderModal = showIframe && !!designCid;

  return renderModal ? (
    <Dialog
      onClose={onClose}
      open={renderModal}
      surfaceSx={{
        height: '100%',
        paddingBottom: 0,
        width: '100%',
        paddingTop: '1rem',
      }}
      title="Select Product"
      titleSx={{
        paddingLeft: '0.75rem',
        paddingBottom: '0.625rem',
        marginTop: '-0.25rem',
      }}
      width="full"
    >
      {modalContents}
    </Dialog>
  ) : null;
}
